import { ThunkDispatch } from "core-integration/src/store";
import { updateElementControls } from "core-integration/src/store/elements/actions";
import { getElementControls } from "core-integration/src/store/elements/reducers";
import { getWatched } from "core-integration/src/store/player/reducers";
import { useEffect, useState } from "react";
import { SortDescriptor } from "react-aria-components";
import { useDispatch, useSelector } from "react-redux";
import {
  PriceChangeElementFilter,
  PriceChangeProgressFilter,
} from "../PriceChangeFilters/utils";

export type PriceChangesView = "predictions" | "history";

const defaultSortDescriptors: Record<PriceChangesView, SortDescriptor> = {
  predictions: { column: "progressMagnitude", direction: "descending" },
  history: { column: "date", direction: "descending" },
};

export const usePriceChangeControls = () => {
  const dispatch = useDispatch<ThunkDispatch>();
  const controls = useSelector(getElementControls);
  const watched = useSelector(getWatched);

  const [activeView, setActiveView] = useState<PriceChangesView>("predictions");
  const [progressFilter, setProgressFilter] =
    useState<PriceChangeProgressFilter>("riseAndDrop");
  const [elementFilter, setElementFilter] =
    useState<PriceChangeElementFilter>("all");
  const [sortDescriptor, setSortDescriptor] = useState<SortDescriptor>(
    defaultSortDescriptors.predictions,
  );
  const [page, setPage] = useState(1);

  const defaultSortDescriptor = defaultSortDescriptors[activeView];
  const isSortModified =
    sortDescriptor.column !== defaultSortDescriptor.column ||
    sortDescriptor.direction !== defaultSortDescriptor.direction;

  const setSearch = (search: string) =>
    dispatch(updateElementControls({ ...controls, search }));

  const resetControls = (view: PriceChangesView) => {
    setProgressFilter("riseAndDrop");
    setElementFilter("all");
    setSortDescriptor(defaultSortDescriptors[view]);
    setPage(1);
    setSearch("");
  };

  const handleViewChange = (keys: Set<PriceChangesView>) => {
    const nextView = Array.from(keys)[0];
    setActiveView(nextView);
    resetControls(nextView);
  };

  const handleProgressFilterChange = (value: PriceChangeProgressFilter) => {
    setProgressFilter(value);
    setSortDescriptor(defaultSortDescriptor);
    setPage(1);
  };

  const handleElementFilterChange = (value: PriceChangeElementFilter) => {
    setElementFilter(value);
    setPage(1);
  };

  const handleSortChange = (descriptor: SortDescriptor) => {
    const isNewColumn = descriptor.column !== sortDescriptor.column;
    // Numeric columns should start high to low on the first click.
    if (isNewColumn && descriptor.column !== "player") {
      setSortDescriptor({ ...descriptor, direction: "descending" });
    } else {
      setSortDescriptor(descriptor);
    }
    setPage(1);
  };

  const handleSearchChange = (value: string) => {
    setSearch(value);
    setPage(1);
  };

  useEffect(() => {
    // The search is shared element state, so may be left over from another page.
    setSearch("");
  }, []);

  useEffect(() => {
    if (elementFilter === "watchlist") setPage(1);
  }, [watched]);

  return {
    view: {
      activeView,
      handleViewChange,
    },
    filters: {
      progressFilter,
      elementFilter,
      onProgressFilterChange: handleProgressFilterChange,
      onElementFilterChange: handleElementFilterChange,
      onReset: () => resetControls(activeView),
    },
    sort: {
      sortDescriptor,
      onSortChange: handleSortChange,
      isSortModified,
    },
    pagination: {
      page,
      setPage,
    },
    search: {
      value: controls.search,
      onChange: handleSearchChange,
    },
  };
};
